import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PropertyCard from '../components/PropertyCard';
import { basesAPI, listingAPI, locationAPI } from '../lib/api';
import { getUnitPreference, setUnitPreference } from '../lib/unitPreference';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, SlidersHorizontal, X, Loader2, MapPin, Clock, Star, Shield, CheckCircle2 } from 'lucide-react';
import { processListingsForDisplay } from '../lib/listingsSearch';
import { getSampleListings, sampleLocations } from '../lib/samplePreviewData';
import { getSamplePreviewEnabled, subscribeToSamplePreviewMode } from '../lib/samplePreviewMode';
import { trackEvent } from '../lib/analytics';

export default function ListingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { subscription } = useAuth();
  const { t, locale } = useLanguage();
  const [listings, setListings] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [samplePreviewEnabled, setSamplePreviewState] = useState(getSamplePreviewEnabled());
  const [baseRegistry, setBaseRegistry] = useState(null);
  const [distanceUnit, setDistanceUnit] = useState(() => getUnitPreference());

  const isPremium = subscription?.tier === 'COMMAND' || subscription?.tier === 'FORTRESS';

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    location_id: searchParams.get('location_id') || 'all',
    bedrooms_min: searchParams.get('bedrooms_min') || 'all',
    availability: searchParams.get('availability') || 'all',
    available_within: searchParams.get('available_within') || 'all',
  });

  useEffect(() => subscribeToSamplePreviewMode(setSamplePreviewState), []);

  useEffect(() => {
    trackEvent('listings_view', { source: 'listings_page' });
  }, []);

  useEffect(() => {
    let cancelled = false;
    basesAPI
      .getAll({ country_code: 'DE' })
      .then((response) => {
        if (!cancelled) setBaseRegistry(response.data);
      })
      .catch((error) => {
        console.error('Failed to load base registry:', error);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDistanceUnitChange = (unit) => {
    setDistanceUnit(unit);
    setUnitPreference(unit);
  };

  useEffect(() => {
    loadLocations();
  }, [samplePreviewEnabled]);

  useEffect(() => {
    loadListings();
  }, [filters, samplePreviewEnabled]);

  const applySampleListings = () => {
    const processed = processListingsForDisplay(getSampleListings(locale), '');
    setListings(processed);
    setLocations(sampleLocations);
    setLoading(false);
  };

  const loadLocations = async () => {
    if (samplePreviewEnabled) {
      setLocations(sampleLocations);
      return;
    }
    try {
      const response = await locationAPI.getAll({ active_only: true });
      setLocations(response.data || []);
    } catch (error) {
      console.error('Failed to load locations:', error);
      // Batch 6: no fake fallback for real visitors — an empty list is honest.
      setLocations([]);
    }
  };

  const loadListings = async () => {
    if (samplePreviewEnabled) {
      applySampleListings();
      return;
    }
    setLoading(true);
    setLoadError(false);
    try {
      const params = { include_booked: true };
      if (filters.search.trim()) params.search = filters.search.trim();
      if (filters.location_id !== 'all') params.location_id = filters.location_id;
      if (filters.bedrooms_min !== 'all') params.bedrooms_min = parseInt(filters.bedrooms_min, 10);
      if (filters.availability !== 'all') params.availability = filters.availability;
      if (filters.available_within !== 'all') params.available_within_days = parseInt(filters.available_within, 10);
      const response = await listingAPI.getAll(params);
      const processed = processListingsForDisplay(response.data || [], '');
      setListings(processed);
    } catch (error) {
      console.error('Failed to load listings:', error);
      // Batch 6: never show fabricated sample listings to real visitors on an API
      // error — surface an honest error state instead (sample data is preview-only).
      setListings([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const nextFilters = { ...filters, [key]: value };
    setFilters(nextFilters);
    const nextParams = new URLSearchParams(searchParams);
    if (value && value !== 'all') nextParams.set(key, value);
    else nextParams.delete(key);
    setSearchParams(nextParams);
  };

  const clearFilters = () => {
    setFilters({ search: '', location_id: 'all', bedrooms_min: 'all', availability: 'all', available_within: 'all' });
    setSearchParams({});
  };

  const hasActiveFilters = Boolean(filters.search || filters.location_id !== 'all' || filters.bedrooms_min !== 'all' || filters.availability !== 'all' || filters.available_within !== 'all');

  const stats = useMemo(() => ({
    readyNow: listings.filter((listing) => listing.availability_status === 'AVAILABLE' || listing.days_until_available === 0).length,
    within90: listings.filter((listing) => Number.isFinite(listing.days_until_available) && listing.days_until_available > 0 && listing.days_until_available <= 90).length,
    knownMonthly: listings.filter((listing) => listing.total_monthly_cost != null).length,
    confirmed: listings.filter((listing) => listing.availability_confidence === 'HIGH').length,
    earlyAccess: listings.filter((listing) => listing.is_early_access).length,
  }), [listings]);

  return (
    <div className="min-h-screen bg-ivory pt-20" data-testid="listings-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 mb-2 tracking-tight">{t('listings.page_title', 'FIND YOUR RELOCATION FIT')}</h1>
          <div className="h-1 w-16 bg-gradient-to-r from-military-gold to-amber-400 rounded-full mb-3" />
          <p className="text-slate-600 max-w-3xl">{t('listings.page_subtitle', 'Germany-first housing with timing, pricing, and trust signals built for PCS and TLA decisions — not generic browsing.')}</p>

          {samplePreviewEnabled && (
            <div className="inline-flex items-center gap-2 px-4 py-2 mt-4 bg-signal-blue/10 border border-signal-blue/20 rounded-full text-sm text-signal-blue">
              {t('listings.sample_preview_banner', 'Sample preview mode is active. Public listings below are demo-ready sample profiles with client-safe photos and trust signals.')}
            </div>
          )}

          <div className="flex flex-wrap gap-3 mt-4">
            {stats.readyNow > 0 && <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-100 border border-emerald-300 rounded-full"><CheckCircle2 className="w-4 h-4 text-emerald-600" /><span className="text-emerald-700 text-sm font-medium">{stats.readyNow} {t('listings.ready_now', 'ready now')}</span></div>}
            {stats.within90 > 0 && <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-100 border border-amber-300 rounded-full"><Clock className="w-4 h-4 text-amber-600" /><span className="text-amber-700 text-sm font-medium">{stats.within90} {t('listings.within_90', 'within 90 days')}</span></div>}
            {stats.confirmed > 0 && <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 border border-blue-300 rounded-full"><Shield className="w-4 h-4 text-blue-600" /><span className="text-blue-700 text-sm font-medium">{stats.confirmed} {t('listings.recently_confirmed', 'recently confirmed')}</span></div>}
            {stats.knownMonthly > 0 && <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 border border-slate-300 rounded-full"><MapPin className="w-4 h-4 text-slate-700" /><span className="text-slate-700 text-sm font-medium">{stats.knownMonthly} {t('listings.with_monthly_total', 'with known monthly total')}</span></div>}
            {isPremium && stats.earlyAccess > 0 && <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-100 border border-purple-300 rounded-full"><Star className="w-4 h-4 text-purple-600 fill-current" /><span className="text-purple-700 text-sm font-medium">{stats.earlyAccess} {t('listings.early_access', 'early access')}</span></div>}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
              <Input type="text" placeholder={t('listings.search_placeholder', 'Search by city, nearby base, or property name...')} value={filters.search} onChange={(event) => handleFilterChange('search', event.target.value)} className="pl-10 bg-white border-slate-300 text-slate-800 placeholder:text-slate-600" data-testid="search-input" />
            </div>
            <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="md:hidden border-slate-300 text-slate-700"><SlidersHorizontal className="w-4 h-4 mr-2" />{t('filters.toggle', 'Filters')}</Button>
            <div className="hidden md:flex gap-3">
              <Select value={filters.location_id} onValueChange={(value) => handleFilterChange('location_id', value)}>
                <SelectTrigger className="w-[180px] bg-white border-slate-300 text-slate-800" data-testid="location-filter"><MapPin className="w-4 h-4 mr-2 text-slate-600" /><SelectValue placeholder={t('filters.all_locations', 'All Locations')} /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200"><SelectItem value="all">{t('filters.all_locations', 'All Locations')}</SelectItem>{locations.map((location) => <SelectItem key={location.id} value={location.id} className="text-slate-700">{location.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={filters.bedrooms_min} onValueChange={(value) => handleFilterChange('bedrooms_min', value)}>
                <SelectTrigger className="w-[140px] bg-white border-slate-300 text-slate-800" data-testid="bedrooms-filter"><SelectValue placeholder={t('filters.bedrooms', 'Bedrooms')} /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200"><SelectItem value="all">{t('filters.any_beds', 'Any Beds')}</SelectItem><SelectItem value="1" className="text-slate-700">1+ {t('listing.beds', 'Beds')}</SelectItem><SelectItem value="2" className="text-slate-700">2+ {t('listing.beds', 'Beds')}</SelectItem><SelectItem value="3" className="text-slate-700">3+ {t('listing.beds', 'Beds')}</SelectItem><SelectItem value="4" className="text-slate-700">4+ {t('listing.beds', 'Beds')}</SelectItem></SelectContent>
              </Select>
              <Select value={filters.availability} onValueChange={(value) => handleFilterChange('availability', value)}>
                <SelectTrigger className="w-[160px] bg-white border-slate-300 text-slate-800" data-testid="availability-filter"><SelectValue placeholder={t('filters.availability', 'Availability')} /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200"><SelectItem value="all">{t('filters.any_status', 'Any Status')}</SelectItem><SelectItem value="AVAILABLE" className="text-slate-700">{t('filters.available_now', 'Available Now')}</SelectItem><SelectItem value="COMING_SOON" className="text-slate-700">{t('filters.coming_soon', 'Coming Soon')}</SelectItem></SelectContent>
              </Select>
              <Select value={filters.available_within} onValueChange={(value) => handleFilterChange('available_within', value)}>
                <SelectTrigger className="w-[180px] bg-white border-slate-300 text-slate-800" data-testid="available-within-filter"><Clock className="w-4 h-4 mr-2 text-slate-600" /><SelectValue placeholder={t('filters.available_within', 'Available Within')} /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200"><SelectItem value="all">{t('filters.any_time', 'Any Time')}</SelectItem><SelectItem value="30" className="text-slate-700">{t('filters.within_30', 'Within 30 days')}</SelectItem><SelectItem value="60" className="text-slate-700">{t('filters.within_60', 'Within 60 days')}</SelectItem><SelectItem value="90" className="text-slate-700">{t('filters.within_90', 'Within 90 days')}</SelectItem></SelectContent>
              </Select>
              {hasActiveFilters && <Button variant="ghost" onClick={clearFilters} className="text-slate-600 hover:text-slate-800" data-testid="clear-filters"><X className="w-4 h-4 mr-1" />{t('filters.clear', 'Clear')}</Button>}
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-4">{t('listings.search_hint', 'Prioritize listings with a known monthly total and recently confirmed availability before you inquire.')}</p>
          {showFilters && (
            <div className="md:hidden mt-4 pt-4 border-t border-slate-200 space-y-3">
              <Select value={filters.location_id} onValueChange={(value) => handleFilterChange('location_id', value)}><SelectTrigger className="w-full bg-white border-slate-300 text-slate-800"><SelectValue placeholder={t('filters.select_location', 'Select Location')} /></SelectTrigger><SelectContent className="bg-white border-slate-200"><SelectItem value="all">{t('filters.all_locations', 'All Locations')}</SelectItem>{locations.map((location) => <SelectItem key={location.id} value={location.id} className="text-slate-700">{location.name}</SelectItem>)}</SelectContent></Select>
              <div className="flex gap-3">
                <Select value={filters.bedrooms_min} onValueChange={(value) => handleFilterChange('bedrooms_min', value)}><SelectTrigger className="flex-1 bg-white border-slate-300 text-slate-800"><SelectValue placeholder={t('filters.bedrooms', 'Bedrooms')} /></SelectTrigger><SelectContent className="bg-white border-slate-200"><SelectItem value="all">{t('filters.any_beds', 'Any')}</SelectItem><SelectItem value="1" className="text-slate-700">1+</SelectItem><SelectItem value="2" className="text-slate-700">2+</SelectItem><SelectItem value="3" className="text-slate-700">3+</SelectItem></SelectContent></Select>
                <Select value={filters.availability} onValueChange={(value) => handleFilterChange('availability', value)}><SelectTrigger className="flex-1 bg-white border-slate-300 text-slate-800"><SelectValue placeholder={t('filters.status', 'Status')} /></SelectTrigger><SelectContent className="bg-white border-slate-200"><SelectItem value="all">{t('filters.any_status', 'Any')}</SelectItem><SelectItem value="AVAILABLE" className="text-slate-700">{t('filters.available_now', 'Available')}</SelectItem><SelectItem value="COMING_SOON" className="text-slate-700">{t('filters.coming_soon', 'Coming Soon')}</SelectItem></SelectContent></Select>
              </div>
              <Select value={filters.available_within} onValueChange={(value) => handleFilterChange('available_within', value)}><SelectTrigger className="w-full bg-white border-slate-300 text-slate-800"><SelectValue placeholder={t('filters.available_within', 'Available Within')} /></SelectTrigger><SelectContent className="bg-white border-slate-200"><SelectItem value="all">{t('filters.any_time', 'Any Time')}</SelectItem><SelectItem value="30" className="text-slate-700">{t('filters.within_30', 'Within 30 days')}</SelectItem><SelectItem value="60" className="text-slate-700">{t('filters.within_60', 'Within 60 days')}</SelectItem><SelectItem value="90" className="text-slate-700">{t('filters.within_90', 'Within 90 days')}</SelectItem></SelectContent></Select>
              {hasActiveFilters && <Button variant="outline" onClick={clearFilters} className="w-full border-slate-300 text-slate-600"><X className="w-4 h-4 mr-2" />{t('filters.clear_all', 'Clear All Filters')}</Button>}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-signal-blue animate-spin" /></div>
        ) : listings.length > 0 ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <p className="text-sm text-slate-600">{t('listings.showing', 'Showing')} {listings.length} {t('listings.properties', 'properties')}</p>
              <div className="flex flex-wrap items-center gap-3">
                {hasActiveFilters && (
                  <p className="text-xs text-slate-500">{t('listings.search_ranked_hint', 'Search is ranked for relocation usefulness, confidence, and timing.')}</p>
                )}
                <div
                  className="inline-flex rounded-md border border-slate-300 overflow-hidden text-xs"
                  role="group"
                  aria-label={t('listings.distance_unit', 'Distance unit')}
                  data-testid="distance-unit-toggle"
                >
                  <button
                    type="button"
                    onClick={() => handleDistanceUnitChange('km')}
                    className={`px-3 py-1.5 font-medium ${distanceUnit === 'km' ? 'bg-signal-blue text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                  >
                    km
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDistanceUnitChange('mi')}
                    className={`px-3 py-1.5 font-medium border-l border-slate-300 ${distanceUnit === 'mi' ? 'bg-signal-blue text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                  >
                    mi
                  </button>
                </div>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.map((listing) => (
                <PropertyCard
                  key={listing.id}
                  listing={listing}
                  baseRegistry={baseRegistry}
                  distanceUnit={distanceUnit}
                />
              ))}
            </div>
          </>
        ) : loadError ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
            <h2 className="font-heading text-2xl font-bold text-slate-800 mb-2">{t('listings.load_error_title', 'We couldn’t load listings just now')}</h2>
            <p className="text-slate-600 mb-6 max-w-2xl mx-auto">{t('listings.load_error_body', 'Something went wrong reaching our servers. Please try again in a moment.')}</p>
            <Button onClick={loadListings} className="bg-signal-blue hover:bg-signal-blue-hover text-white">{t('listings.retry', 'Retry')}</Button>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
            <h2 className="font-heading text-2xl font-bold text-slate-800 mb-2">{t('listings.no_results_title', 'No matching homes found')}</h2>
            <p className="text-slate-600 mb-6 max-w-2xl mx-auto">{t('listings.no_results_body', 'Try widening your timing or availability filters. Germany-first relocation inventory is strongest when you compare ready-now homes with listings becoming available within 30 to 90 days.')}</p>
            {hasActiveFilters && <Button onClick={clearFilters} className="bg-signal-blue hover:bg-signal-blue-hover text-white">{t('filters.clear_all', 'Clear Filters')}</Button>}
          </div>
        )}
      </div>
    </div>
  );
}
